/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { DefaultTheme } from "styled-components";

/* -------------------------------------------------- */
/*                      Colors                        */
/* -------------------------------------------------- */
export enum Color {
  White = "#ffffff",
  Alabaster = "#fbfbfb",
  Gray1 = "#EAEAEA",
  Gray2 = "#CCCCCC",
  Gray3 = "#AEAEAE",
  Gray4 = "#494949",
  Gray5 = "#737373",
  Black = "#000000",
  CopperRust = "#AF5154",
}

/* -------------------------------------------------- */
/*                Consistent Theming                  */
/* -------------------------------------------------- */
const consistentTheming = {
  fonts: { primary: "Inter", monospace: "IBM Plex Mono" },
  sizing: { tiny: "0.8rem", small: "1rem" },
};
/* -------------------------------------------------- */
/*                      Themes                        */
/* -------------------------------------------------- */

export const lightTheme: DefaultTheme = {
  background: Color.Alabaster,
  primary: Color.Black,
  accent: Color.CopperRust,
  text: {
    default: Color.Black,
    subtle: Color.Gray3,
    url: "#0000ee",
    disabled: Color.Gray1,
    highContrast: Color.White,
  },
  block: { border: Color.Gray2 },
  mobileAppTopbarColor: Color.White,
  subtleContrast: Color.Gray1,
  button: { default: Color.Alabaster, hover: Color.Gray1, active: Color.Gray2, disabled: Color.Gray3 },
  highContrastButton: {
    default: Color.Black,
    hover: Color.Gray4,
    active: Color.Gray5,
    disabled: Color.Gray4,
  },
  iconButton: {
    color: Color.Gray2,
    hoverColor: Color.Gray3,
    activeColor: Color.Alabaster,
    activeBackgroundColor: Color.Gray5,
    selected: { color: Color.Black, backgroundColor: Color.Gray1, activeColor: Color.Gray2 },
  },
  scrollBar: { default: Color.Gray2, hover: Color.Gray3 },
  canvas: {
    grid: "#d9d9d9",
    create: "rgba(0, 0, 0, 0.07)",
    dragProhibited: "rgba(228, 130, 130, 0.205)",
    select: "rgba(0, 133, 255, .07)",
  },
  modal: { background: "rgba(251, 251, 251, 0.8)", border: Color.Black },
  backgroundAccent: "#f6f6f6",
  blurMenu: { background: "rgba(255, 255, 255, 0.9)", border: "#000000", text: "#000000" },
  ...consistentTheming,
};

export const darkTheme: DefaultTheme = {
  background: "#282828",
  primary: Color.Alabaster,
  accent: Color.CopperRust,
  text: {
    default: Color.Alabaster,
    subtle: Color.Gray3,
    url: "#55ADFF",
    disabled: Color.Gray3,
    highContrast: Color.Black,
  },
  block: { border: Color.Gray4 },
  mobileAppTopbarColor: "#282828",
  subtleContrast: Color.Gray4,
  button: { default: "#282828", hover: Color.Gray4, active: Color.Gray5, disabled: Color.Gray3 },
  highContrastButton: {
    default: Color.Alabaster,
    hover: Color.Gray1,
    active: Color.Gray2,
    disabled: Color.Gray3,
  },
  iconButton: {
    color: Color.Gray5,
    hoverColor: Color.Gray3,
    activeColor: Color.Black,
    activeBackgroundColor: Color.Gray1,
    selected: { color: Color.Alabaster, backgroundColor: Color.Gray5, activeColor: Color.Gray4 },
  },
  scrollBar: { default: Color.Gray5, hover: Color.Gray3 },
  canvas: {
    grid: "#5a5a5a",
    create: "rgba(255, 255, 255, 0.17)",
    dragProhibited: "rgba(226, 127, 127, 0.4)",
    select: "rgba(0, 133, 255, .07)",
  },
  modal: { background: "rgba(0, 0, 0, 0.8)", border: "#FFFFFF" },
  backgroundAccent: "#393939",
  blurMenu: { background: "rgba(0, 0, 0, 0.9)", border: "#ffffff", text: "#ffffff" },
  ...consistentTheming,
};
